import React, { useState, useEffect, useContext } from "react";
import * as moment from "moment";
import { useParams } from "react-router-dom";
import config from "config/config";

import "./DailyAttendance.css";
import DailyAttendanceImg from "assets/images/DailyAttendance.png";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import Box from "@mui/material/Box";
import Context from "context/context";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import attendanceAPI from "../../../apis/panel/Attendance/attendance.js"


const DailyAttendance = () => {
  const { dispatch } = useContext(Context);
  const [attendanceList, setAttendanceList] = useState([]);
  const [showAll, setShowAll] = useState(false);

   const {getTodaysAttendanceAPI} = attendanceAPI()

  const [now, setNow] = useState(moment());
  useEffect(() => {
    const timeUpdater = setInterval(() => {
      setNow(moment());
    }, 1000);

    const polling = setInterval(() => {
      fetchAttendance(true); // Fetch updated records every 30 sec
    }, 30000);

    return () => {
      clearInterval(timeUpdater);
      clearInterval(polling);
    };
  }, []);

  useEffect(() => {
    fetchAttendance();
  }, []);

  const fetchAttendance = async (silent = false) => {
    if (!silent) {
      dispatch({ type: "SET_LOADING", payload: true });
    }

    try {
     
      const result = await getTodaysAttendanceAPI();


      if (result.status === "Ok") {
        setAttendanceList(result.data);
      }
    } catch (error) {
      console.error("Error fetching attendance:", error);
    } finally {
      if (!silent) {
        dispatch({ type: "SET_LOADING", payload: false });
      }
    }
  };

  const formatDuration = (secs) => {
    if (!secs || isNaN(secs)) return "00h 00m";
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    // const s = Math.floor(secs % 60);
    return `${h.toString().padStart(2, "0")}h ${m.toString().padStart(2, "0")}m `;
  };

  const calculateSegmentsWithGaps = (records, now) => {
    const sorted = [...records].sort((a, b) =>
      moment(a.accessTime, "HH:mm:ss").diff(moment(b.accessTime, "HH:mm:ss"))
    );

    const segments = [];
    let timelineStart = null;

    for (let i = 0; i < sorted.length - 1; i++) {
      const current = sorted[i];
      const next = sorted[i + 1];

      if (current.status === "Check-In" && next.status === "Check-Out") {
        const checkIn = moment(current.accessTime, "HH:mm:ss");
        const checkOut = moment(next.accessTime, "HH:mm:ss");

        if (checkOut.isAfter(checkIn)) {
          if (!timelineStart || checkIn.isBefore(timelineStart)) {
            timelineStart = checkIn;
          }
          segments.push({
            checkIn,
            checkOut,
            label: `${checkIn.format("h:mm:ss A")} - ${checkOut.format("h:mm:ss A")}`,
          });
          i++;
        }
      }
    }

    const last = sorted[sorted.length - 1];
    if (last?.status === "Check-In") {
      const checkIn = moment(last.accessTime, "HH:mm:ss");
      const checkOut = now;
      if (!timelineStart || checkIn.isBefore(timelineStart)) {
        timelineStart = checkIn;
      }
      segments.push({
        checkIn,
        checkOut,
        label: `${checkIn.format("h:mm:ss A")} - ${checkOut.format("h:mm:ss A")} (Now)`
      });
    }

    if (!timelineStart) return { segments: [], timelineDuration: 0, timelineStart: null };

    // Work seconds
    const totalWorkSeconds = segments.reduce(
      (sum, seg) => sum + seg.checkOut.diff(seg.checkIn, "seconds"),
      0
    );

    // Gap seconds
    let totalGapSeconds = 0;
    for (let i = 0; i < segments.length - 1; i++) {
      const gap = segments[i + 1].checkIn.diff(segments[i].checkOut, "seconds");
      if (gap > 0) totalGapSeconds += gap;
    }

    const requiredWorkSeconds = 8 * 60 * 60; // 8 hours in seconds

    const timelineDuration =
      (totalWorkSeconds >= requiredWorkSeconds ? totalWorkSeconds : requiredWorkSeconds) +
      totalGapSeconds;

    const enrichedSegments = segments.map((seg) => {
      const leftPercent =
        (seg.checkIn.diff(timelineStart, "seconds") / timelineDuration) * 100;
      const widthPercent =
        (seg.checkOut.diff(seg.checkIn, "seconds") / timelineDuration) * 100;
      return {
        ...seg,
        leftPercent,
        widthPercent,
      };
    });

    return {
      segments: enrichedSegments,
      timelineStart,
      timelineDuration,
      totalSeconds: totalWorkSeconds,
    };
  };

  return (
    <Box
      sx={{
        mt: 2,
        boxShadow: "rgba(0, 0, 0, 0.24) 0px 3px 8px",
        p: 2,
        borderRadius: 2,
      }}
    >
      {/* Top heading */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <Box sx={{ pr: 2 }}>
          <img
            className="personalInfo-logo"
            src={DailyAttendanceImg}
            alt="info"
          />
        </Box>
        <Box className="acc-title" sx={{ fontSize: "20px", fontWeight: "bold" }}>
          Employee Office In-Time
        </Box>
      </Box>

      {/* Attendance Bars */}
      {(showAll ? attendanceList : attendanceList.slice(0, 1)).map(
        (item, index) => {
          const {
            segments,
            timelineStart,
            timelineDuration,
            totalSeconds,
          } = calculateSegmentsWithGaps(item.records, now);

          const isMoreThan8Hours = totalSeconds >= 8 * 60 * 60;

          const firstCheckIn = item.records
            .filter((entry) => entry.status === "Check-In")
            .sort(
              (a, b) =>
                moment(a.accessTime, "HH:mm:ss") -
                moment(b.accessTime, "HH:mm:ss")
            )[0]?.accessTime;

          const sortedRecords = [...item.records].sort((a, b) =>
            moment(a.accessTime, "HH:mm:ss").diff(moment(b.accessTime, "HH:mm:ss"))
          );

          const lastRecord = sortedRecords[sortedRecords.length - 1];
          const isCurrentlyCheckedIn = lastRecord?.status === "Check-In";

          const lastCheckOut = isCurrentlyCheckedIn
            ? null
            : sortedRecords
              .filter((entry) => entry.status === "Check-Out")
              .map((entry) => entry.accessTime)
              .sort((a, b) => moment(b, "HH:mm:ss") - moment(a, "HH:mm:ss"))[0] || null;


          return (
            <Box key={index} sx={{ mt: 3 }}>
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                {/* Working hours box */}
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 1,
                    px: 2,
                    py: 1,
                    backgroundColor: "#e8f0fe",
                    borderRadius: "8px",
                    boxShadow: "0 1px 4px rgba(0,0,0,0.1)",
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "#333",
                    whiteSpace: "nowrap",
                    minWidth: "200px",
                  }}
                >
                  <AccessTimeIcon sx={{ color: "#1976d2", fontSize: 20 }} />
                  <span style={{ color: "#1976d2", fontWeight: 600 }}>
                    {moment(item.date).format("DD MMM YYYY")} -{" "}
                    {formatDuration(totalSeconds)}
                  </span>
                  {/* {isMoreThan8Hours && (
                   <span
                     style={{ color: "green", fontSize: "18px", marginLeft: 4 }}
                   >
                     ✅
                   </span>
                 )} */}
                </Box>

                {/* Bar + Labels */}
                <Box sx={{ flexGrow: 1 }}>
                  <Box sx={{ width: "100%" }}>
                    <div className="timeline-container">
                      <div
                        className="timeline-progress-bar"
                        style={{ position: "relative", width: "100%" }}
                      >
                        {segments.map((seg, i) => (
                          <div
                            key={i}
                            className={`timeline-segment ${isMoreThan8Hours ? "green" : "orange"
                              }`}
                            data-label={seg.label}
                            style={{
                              width: `${seg.widthPercent}%`,
                              position: "absolute",
                              left: `${seg.leftPercent}%`,
                              top: 0,
                              bottom: 0,
                            }}
                            title={seg.label}
                          ></div>
                        ))}

                        {item.records
                          .filter((entry) => entry.status === "Check-Out")
                          .map((entry, i) => {
                            const percent =
                              (moment(entry.accessTime, "HH:mm:ss").diff(timelineStart, "seconds") /
                                timelineDuration) *
                              100;
                            return (
                              <div
                                key={`marker-${i}`}
                                className="checkout-marker"
                                style={{
                                  left: `${percent}%`,
                                  transform: "translateX(-50%)",
                                }}
                              />
                            );
                          })}
                      </div>

                      {/* Labels below bar */}
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          marginTop: "8px",
                          fontWeight: "bold",
                          fontSize: "13px",
                          color: "#555",
                          width: "100%",
                        }}
                      >
                        <div>
                          Check-In:{" "}
                          {firstCheckIn
                            ? moment(firstCheckIn, "HH:mm:ss").format("h:mm A")
                            : "--:--"}
                        </div>
                        <div>
                          Last Check-Out:{" "}
                          {lastCheckOut && moment(lastCheckOut, "HH:mm:ss").isValid()
                            ? moment(lastCheckOut, "HH:mm:ss").format("h:mm A")
                            : "--:--"}
                        </div>
                      </div>
                    </div>
                  </Box>
                </Box>
              </Box>
            </Box>
          );
        }
      )}

      {/* Show More/Less Toggle */}
      {attendanceList.length > 1 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            mt: 3,
          }}
        >
          <Box
            onClick={() => setShowAll((prev) => !prev)}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              cursor: "pointer",
              color: "#1976d2",
              fontWeight: 500,
              fontSize: "14px",
            }}
          >
            {showAll ? "Show Less" : "Show All"}
            {showAll ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
          </Box>
        </Box>

      )}
    </Box>
  );


};

export default DailyAttendance;